/**
 * Core conversion functions between YAML, PipelineConfiguration, and React Flow formats
 */
import { logger } from '@/utils';
import type { Edge, Node } from '@xyflow/react';
import { ZodError, type ZodIssue } from 'zod';

import type {
  PipelineBlock,
  PipelineConfiguration,
  PipelineConnection,
} from '@mai/types/src/pipeline-configuration';
import { PipelineConfigurationSchema } from '@mai/types/src/pipeline-configuration';

import {
  calculateAutoLayout,
  createEdgeFromConnection,
  createNodeFromBlock,
  extractBlockFromNode,
  extractConnectionFromEdge,
} from './react-flow-adapters';
import {
  type ParseResult,
  parseYamlSafely,
  stringifyYaml,
  validateConnectionReferences,
  validateYamlStructure,
} from './yaml-helpers';

/**
 * Convert YAML string to validated PipelineConfiguration
 */
export function yamlToPipelineConfig(
  yamlText: string,
): ParseResult<PipelineConfiguration> {
  // First, parse the YAML
  const parseResult = parseYamlSafely(yamlText);
  if (!parseResult.success) {
    return {
      success: false,
      error: parseResult.error,
    };
  }

  // Validate basic YAML structure
  const structureResult = validateYamlStructure(parseResult.data);
  if (!structureResult.success) {
    return {
      success: false,
      error: structureResult.error,
    };
  }

  // Validate connection references
  const referencesResult = validateConnectionReferences(
    structureResult.data as Record<string, unknown>,
  );
  if (!referencesResult.success) {
    return {
      success: false,
      error: referencesResult.error,
    };
  }

  // Validate against Zod schema
  try {
    const validatedConfig = PipelineConfigurationSchema.parse(parseResult.data);
    return {
      success: true,
      data: validatedConfig,
    };
  } catch (error: unknown) {
    const errorMessage =
      error instanceof ZodError
        ? error.errors
            .map((e: ZodIssue) => `${e.path.join('.')}: ${e.message}`)
            .join('; ')
        : error instanceof Error
          ? error.message
          : 'Schema validation failed';

    return {
      success: false,
      error: `Configuration validation error: ${errorMessage}`,
    };
  }
}

/**
 * Best-effort conversion of YAML to PipelineConfiguration
 * Returns partial configuration even when there are validation errors
 */
export function yamlToPipelineConfigBestEffort(
  yamlText: string,
): ParseResult<PipelineConfiguration> & { warnings?: string[] } {
  const warnings: string[] = [];

  // First, try to parse the YAML
  const parseResult = parseYamlSafely(yamlText);
  if (!parseResult.success) {
    return {
      success: false,
      error: parseResult.error,
    };
  }

  let data = parseResult.data as PipelineConfiguration;

  // Try to create a minimal valid structure if basic structure is missing
  if (typeof data !== 'object' || data === null) {
    warnings.push('Invalid root structure, using fallback configuration');
    data = {
      id: 'fallback_pipeline',
      name: 'Fallback Pipeline',
      version: '1.0.0',
      description: 'Generated from invalid YAML',
      blocks: {},
      connections: [],
    };
  }

  // Ensure required top-level properties exist
  if (!data.blocks || typeof data.blocks !== 'object') {
    warnings.push('Missing or invalid "blocks" property, using empty blocks');
    data.blocks = {};
  }

  if (!Array.isArray(data.connections)) {
    warnings.push(
      'Missing or invalid "connections" property, using empty connections',
    );
    data.connections = [];
  }

  // Add default metadata if missing
  if (!data.id || typeof data.id !== 'string') {
    data.id = 'generated_pipeline';
    warnings.push('Missing pipeline ID, using generated ID');
  }

  if (!data.name || typeof data.name !== 'string') {
    data.name = 'Untitled Pipeline';
    warnings.push('Missing pipeline name, using default name');
  }

  if (!data.version || typeof data.version !== 'string') {
    data.version = '1.0.0';
    warnings.push('Missing pipeline version, using default version');
  }

  if (!data.description || typeof data.description !== 'string') {
    data.description = 'Generated pipeline configuration';
    warnings.push('Missing pipeline description, using default description');
  }

  // Try to fix blocks structure
  const validBlocks: Record<string, PipelineBlock> = {};
  const blocksObj = data.blocks as Record<string, PipelineBlock>;

  for (const [blockId, blockData] of Object.entries(blocksObj)) {
    if (typeof blockData === 'object' && blockData !== null) {
      const block = { ...blockData };

      // Ensure block has required properties
      if (!block.id) {
        block.id = blockId;
        warnings.push(`Block "${blockId}" missing ID, using key as ID`);
      }

      if (!block.type || typeof block.type !== 'string') {
        (block as { type: string }).type = 'input';
        warnings.push(
          `Block "${blockId}" missing or invalid type, using "input"`,
        );
      }

      if (!block.config || typeof block.config !== 'object') {
        block.config = {};
        warnings.push(`Block "${blockId}" missing config, using empty config`);
      }

      validBlocks[blockId] = block;
    } else {
      warnings.push(`Block "${blockId}" has invalid structure, skipping`);
    }
  }

  data.blocks = validBlocks;

  // Try to fix connections structure
  const validConnections: PipelineConnection[] = [];

  if (Array.isArray(data.connections)) {
    for (let i = 0; i < data.connections.length; i++) {
      const connection = data.connections[i];

      if (typeof connection === 'object' && connection !== null) {
        const requiredFields = [
          'source_block_id',
          'source_port',
          'target_block_id',
          'target_port',
        ];
        const hasAllFields = requiredFields.every(
          (field) =>
            connection[field as keyof typeof connection] &&
            typeof connection[field as keyof typeof connection] === 'string',
        );

        if (hasAllFields) {
          // Check if referenced blocks exist
          if (
            validBlocks[connection.source_block_id] &&
            validBlocks[connection.target_block_id]
          ) {
            validConnections.push(connection as PipelineConnection);
          } else {
            warnings.push(
              `Connection ${i} references non-existent block(s), skipping`,
            );
          }
        } else {
          warnings.push(`Connection ${i} missing required fields, skipping`);
        }
      } else {
        warnings.push(`Connection ${i} has invalid structure, skipping`);
      }
    }
  }

  data.connections = validConnections as PipelineConnection[];

  // Try Zod validation, but don't fail if it doesn't pass
  try {
    const validatedConfig = PipelineConfigurationSchema.parse(data);
    return {
      success: true,
      data: validatedConfig,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  } catch (error: unknown) {
    // Zod validation failed, but we still return the best-effort result
    const errorMessage =
      error instanceof ZodError
        ? error.errors
            .map((e: ZodIssue) => `${e.path.join('.')}: ${e.message}`)
            .join('; ')
        : error instanceof Error
          ? error.message
          : 'Schema validation failed';

    warnings.push(`Schema validation failed: ${errorMessage}`);

    // Return the partial configuration anyway
    return {
      success: true, // Mark as success since we have a usable configuration
      data: data as PipelineConfiguration,
      warnings,
    };
  }
}

/**
 * Convert PipelineConfiguration to YAML string
 */
export function pipelineConfigToYaml(config: PipelineConfiguration): string {
  try {
    // Validate the configuration first
    const validated = PipelineConfigurationSchema.parse(config);

    // Convert to YAML with consistent formatting
    return stringifyYaml(validated);
  } catch (error) {
    throw new Error(
      `Failed to convert configuration to YAML: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Convert PipelineConfiguration to React Flow format
 */
export function pipelineConfigToReactFlow(
  config: PipelineConfiguration,
  options?: {
    useDagre?: boolean;
    nodePositions?: Map<string, { x: number; y: number }>;
  },
): {
  nodes: Node[];
  edges: Edge[];
} {
  try {
    // Validate the configuration
    const validated = PipelineConfigurationSchema.parse(config);

    // Convert blocks object to array for layout calculation
    const blocksArray = Object.values(validated.blocks);

    logger.debug('pipelineConfigToReactFlow: Converting pipeline config', {
      blockCount: blocksArray.length,
      connectionCount: validated.connections.length,
      hasNodePositions: !!options?.nodePositions,
      nodePositionsCount: options?.nodePositions?.size || 0,
    });

    // If there are no blocks, return empty arrays
    if (blocksArray.length === 0) {
      logger.debug('pipelineConfigToReactFlow: No blocks to convert');
      return { nodes: [], edges: [] };
    }

    // Use stored positions first, fall back to automatic layout
    let positions: Map<string, { x: number; y: number }>;

    if (options?.nodePositions && options.nodePositions.size > 0) {
      // Use stored positions for blocks that have them, calculate for new blocks
      positions = new Map();
      const blocksWithoutPositions: PipelineBlock[] = [];

      for (const block of blocksArray) {
        const storedPosition = options.nodePositions.get(block.id);
        if (storedPosition) {
          positions.set(block.id, storedPosition);
          logger.debug(`Using stored position for block ${block.id}:`, storedPosition);
        } else {
          blocksWithoutPositions.push(block);
          logger.debug(`No stored position for block ${block.id}`);
        }
      }

      // Calculate positions for blocks that don't have stored positions
      if (blocksWithoutPositions.length > 0) {
        logger.debug('Calculating auto-layout for blocks without positions:', {
          count: blocksWithoutPositions.length,
          blockIds: blocksWithoutPositions.map(b => b.id),
        });
        const newPositions = calculateAutoLayout(
          blocksWithoutPositions,
          validated.connections,
          options?.useDagre,
        );

        // Merge with existing positions
        for (const [blockId, position] of newPositions) {
          positions.set(blockId, position);
          logger.debug(`Auto-layout position for block ${blockId}:`, position);
        }
      }
    } else {
      // No stored positions, calculate layout for all blocks
      positions = calculateAutoLayout(
        blocksArray,
        validated.connections,
        options?.useDagre,
      );
    }

    // Create nodes from blocks
    const nodes: Node[] = blocksArray.map((block) => {
      const position = positions.get(block.id) || { x: 0, y: 0 };
      logger.debug(`Creating node for block ${block.id} at position:`, position);
      return createNodeFromBlock(block, position);
    });

    // Create edges from connections
    const edges: Edge[] = validated.connections.map((connection) =>
      createEdgeFromConnection(connection),
    );

    logger.debug('pipelineConfigToReactFlow: Conversion complete', {
      nodeCount: nodes.length,
      edgeCount: edges.length,
    });

    return { nodes, edges };
  } catch (error) {
    throw new Error(
      `Failed to convert configuration to React Flow: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Convert React Flow format to PipelineConfiguration
 */
export function reactFlowToPipelineConfig(
  nodes: Node[],
  edges: Edge[],
): ParseResult<PipelineConfiguration> {
  try {
    // Validate input
    if (!Array.isArray(nodes)) {
      return {
        success: false,
        error: 'Nodes must be an array',
      };
    }

    if (!Array.isArray(edges)) {
      return {
        success: false,
        error: 'Edges must be an array',
      };
    }

    // Extract blocks from nodes as an object with block IDs as keys
    const blocks: Record<string, PipelineBlock> = {};
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      try {
        const block = extractBlockFromNode(node);
        blocks[block.id] = block;
      } catch (error) {
        return {
          success: false,
          error: `Failed to extract block from node at index ${i}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
      }
    }

    // Extract connections from edges
    const connections: PipelineConnection[] = [];
    for (let i = 0; i < edges.length; i++) {
      const edge = edges[i];
      try {
        const connection = extractConnectionFromEdge(edge, nodes);
        connections.push(connection);
      } catch (error) {
        return {
          success: false,
          error: `Failed to extract connection from edge at index ${i}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
      }
    }

    // Create pipeline configuration
    const config: PipelineConfiguration = {
      id: 'converted_pipeline',
      name: 'Converted Pipeline',
      version: '1.0.0',
      description: 'Pipeline converted from React Flow',
      blocks,
      connections,
    };

    // Validate the resulting configuration
    try {
      const validated = PipelineConfigurationSchema.parse(config);
      return {
        success: true,
        data: validated,
      };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof ZodError
          ? error.errors
              .map((e: ZodIssue) => `${e.path.join('.')}: ${e.message}`)
              .join('; ')
          : error instanceof Error
            ? error.message
            : 'Schema validation failed';

      return {
        success: false,
        error: `Generated configuration validation error: ${errorMessage}`,
      };
    }
  } catch (error) {
    return {
      success: false,
      error: `Conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Validate that a React Flow structure can be converted to a valid pipeline
 */
export function validateReactFlowStructure(
  nodes: Node[],
  edges: Edge[],
): ParseResult<{ nodes: Node[]; edges: Edge[] }> {
  // Check for required node properties
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];

    if (!node.id || typeof node.id !== 'string') {
      return {
        success: false,
        error: `Node at index ${i} must have a string "id" property`,
      };
    }

    if (!node.data || typeof node.data !== 'object') {
      return {
        success: false,
        error: `Node "${node.id}" must have a "data" object`,
      };
    }

    if (!node.data.blockType || typeof node.data.blockType !== 'string') {
      return {
        success: false,
        error: `Node "${node.id}" data must include "blockType" property`,
      };
    }

    if (!node.data.config || typeof node.data.config !== 'object') {
      return {
        success: false,
        error: `Node "${node.id}" data must include "config" object`,
      };
    }

    // Validate block type is one of the supported types
    const supportedTypes = [
      // IO Blocks
      'constant',
      'input',
      'output',
      'dataset',
      'file',
      // Flow Blocks
      'identity',
      'zip',
      'map',
      'join',
      // AI Blocks
      'llm',
      'embeddings',
      'semantic_filter',
      // Transform Blocks
      'string_template',
      'text_splitter',
      'transform',
      'pdf_reader',
    ];
    if (!supportedTypes.includes(node.data.blockType)) {
      return {
        success: false,
        error: `Node "${node.id}" has unsupported block type "${node.data.blockType}". Supported types: ${supportedTypes.join(', ')}`,
      };
    }
  }

  // Check for valid edge properties
  const nodeIds = new Set(nodes.map((n) => n.id));

  for (let i = 0; i < edges.length; i++) {
    const edge = edges[i];

    if (!edge.id || typeof edge.id !== 'string') {
      return {
        success: false,
        error: `Edge at index ${i} must have a string "id" property`,
      };
    }

    if (!edge.source || typeof edge.source !== 'string') {
      return {
        success: false,
        error: `Edge "${edge.id}" must have a string "source" property`,
      };
    }

    if (!edge.target || typeof edge.target !== 'string') {
      return {
        success: false,
        error: `Edge "${edge.id}" must have a string "target" property`,
      };
    }

    // Validate that source and target nodes exist
    if (!nodeIds.has(edge.source)) {
      return {
        success: false,
        error: `Edge "${edge.id}" references non-existent source node "${edge.source}"`,
      };
    }

    if (!nodeIds.has(edge.target)) {
      return {
        success: false,
        error: `Edge "${edge.id}" references non-existent target node "${edge.target}"`,
      };
    }
  }

  return {
    success: true,
    data: { nodes, edges },
  };
}

/**
 * Perform a round-trip conversion test to validate data integrity
 */
export function validateRoundTrip(
  config: PipelineConfiguration,
): ParseResult<PipelineConfiguration> {
  try {
    // Convert to YAML and back
    const yamlString = pipelineConfigToYaml(config);
    const yamlResult = yamlToPipelineConfig(yamlString);

    if (!yamlResult.success) {
      return {
        success: false,
        error: `YAML round-trip failed: ${yamlResult.error}`,
      };
    }

    // Convert to React Flow and back
    const reactFlow = pipelineConfigToReactFlow(config);
    const reactFlowResult = reactFlowToPipelineConfig(
      reactFlow.nodes,
      reactFlow.edges,
    );

    if (!reactFlowResult.success) {
      return {
        success: false,
        error: `React Flow round-trip failed: ${reactFlowResult.error}`,
      };
    }

    // Basic comparison (simplified - in production might want deep equality check)
    const original = config;
    const fromYaml = yamlResult.data!;
    const fromReactFlow = reactFlowResult.data!;

    if (
      Object.keys(original.blocks).length !==
        Object.keys(fromYaml.blocks).length ||
      Object.keys(original.blocks).length !==
        Object.keys(fromReactFlow.blocks).length
    ) {
      return {
        success: false,
        error: 'Round-trip conversion changed the number of blocks',
      };
    }

    if (
      original.connections.length !== fromYaml.connections.length ||
      original.connections.length !== fromReactFlow.connections.length
    ) {
      return {
        success: false,
        error: 'Round-trip conversion changed the number of connections',
      };
    }

    return {
      success: true,
      data: config,
    };
  } catch (error) {
    return {
      success: false,
      error: `Round-trip validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}
